import type { AxiosResponse } from "axios"
import { debounce } from "lodash-es"
import {
  useCallback,
  useMemo,
  useRef,
} from "react"

type Updater<T> = (updates: T) => Promise<AxiosResponse>
type DebouncedUpdater<T> = (updates: T) => void

const defaultDebounceTime = 250
const defaultMaxWait = 5000

interface DebouncedUpdaterConfig<T extends object> {
  updater?: Updater<T>;
  debounceTime?: number;
  maxWait?: number;
  onError?: (error: unknown) => void;
}

// TODO move this
// takes an updater function and returns a debounced version of it
export function useDebouncedUpdater<T extends object>(config: DebouncedUpdaterConfig<T>): DebouncedUpdater<T> {
  const {
    updater,
    onError,
    debounceTime = defaultDebounceTime,
    maxWait = defaultMaxWait,
  } = config

  const unsavedChangesRef = useRef<T | null>(null)

  const debouncedUpdater = useMemo(
    () => debounce(
      async () => {
        const unsavedChanges = unsavedChangesRef.current

        try {
          if (!updater) {
            throw new Error("updater is not defined")
          }

          // clear the unsaved changes
          unsavedChangesRef.current = null

          // make request
          if (unsavedChanges) {
            await updater(unsavedChanges)
          }
        } catch (e) {
          onError?.(e)

          // re-store the unsaved changes if the request fails
          unsavedChangesRef.current = {
            ...unsavedChanges,
            ...(unsavedChangesRef.current || {}),
          } as T
        }
      },
      debounceTime,
      { maxWait },
    ),
    [
      updater,
      debounceTime,
      maxWait,
      onError,
    ],
  )

  // this function stashes the changes and calls the debounced udpater
  return useCallback(
    (updates: T) => {
      // store the unsaved changes
      unsavedChangesRef.current = {
        ...unsavedChangesRef.current,
        ...updates,
      }

      void debouncedUpdater()
    },
    [debouncedUpdater],
  )
}
